import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckOutlined, ContentCopyOutlined } from "@mui/icons-material";

const normalizeProfileImageUrl = (value = "") => {
  const url = `${value || ""}`.trim();
  if (!url || ["undefined", "null", "[object object]"].includes(url.toLowerCase())) return "";
  return url;
};

const readProfileImage = (profile = {}) =>
  normalizeProfileImageUrl(
    profile.avatarUrl
    || profile.avatar_url
    || profile.photoUrl
    || profile.imageUrl
    || profile.profileImage?.url
    || profile.profileImage?.secure_url
    || profile.profileImage?.secureUrl
    || profile.photo?.url
    || profile.photo?.secure_url
    || profile.photo?.secureUrl
    || profile.avatar?.url
    || profile.avatar?.secure_url
    || profile.avatar?.secureUrl
    || (typeof profile.avatar === "string" ? profile.avatar : "")
    || ""
  );

const readProfileName = (profile = {}) =>
  profile.name
  || profile.fullName
  || profile.displayName
  || profile.email
  || profile.handle
  || "Workspace user";

const getInitials = (name = "") => {
  const words = `${name}`.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "U";
  return words.slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join("");
};

// The profile card is portalled outside its trigger. This brief grace period lets
// users cross the visual gap, without leaving older profile cards on screen.
const hoverCardCloseDelayMs = 250;
let activeWorkspaceUserHoverCard = null;

export const normalizeWorkspaceUserProfile = (user = {}) => {
  const profile = typeof user === "string" ? { name: user } : (user || {});
  const name = readProfileName(profile);
  const email = profile.email || profile.workEmail || profile.primaryEmail || "";
  const role = profile.designation || profile.title || profile.workspaceRole || profile.role || "Workspace member";
  const meta = [profile.department, profile.team, profile.location].filter(Boolean).join(" · ");

  return {
    ...profile,
    avatarUrl: readProfileImage(profile),
    email,
    initials: getInitials(name),
    meta: meta || "Lyriclious Workspace",
    name,
    role,
  };
};

export function WorkspaceUserAvatar({ className = "", title = "", user }) {
  const profile = normalizeWorkspaceUserProfile(user);
  const isUnassigned = `${title || profile.name || ""}`.trim().toLowerCase() === "unassigned";
  const [imageUrl, setImageUrl] = useState(profile.avatarUrl);

  useEffect(() => {
    setImageUrl(profile.avatarUrl);
  }, [profile.avatarUrl]);

  return (
    <span className={`workspace-user-avatar ${className}`.trim()} aria-hidden="true" title={title || undefined}>
      {imageUrl ? (
        <img alt="" src={imageUrl} onError={() => setImageUrl("")} />
      ) : isUnassigned ? (
        "?"
      ) : (
        profile.initials
      )}
    </span>
  );
}

export function WorkspaceUserHoverCard({ children, className = "", placement = "top", user }) {
  const profile = normalizeWorkspaceUserProfile(user);
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ left: 12, top: 12, width: 280 });
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);
  const closeTimerRef = useRef(null);
  const hoverCardTokenRef = useRef({});

  const updatePopoverPosition = useCallback((popoverHeight = 172, popoverWidth = 280) => {
    const anchor = anchorRef.current;
    if (!anchor || typeof window === "undefined") return;

    const rect = anchor.getBoundingClientRect();
    const width = Math.min(popoverWidth, Math.max(220, window.innerWidth - 24));
    const shouldOpenBelow = placement === "bottom" || rect.top < popoverHeight + 16;
    setPopoverPosition({
      left: Math.max(12, Math.min(rect.left + (rect.width / 2) - (width / 2), window.innerWidth - width - 12)),
      top: shouldOpenBelow ? rect.bottom + 10 : Math.max(12, rect.top - popoverHeight - 10),
      width,
    });
  }, [placement]);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const openPopover = useCallback(() => {
    if (activeWorkspaceUserHoverCard && activeWorkspaceUserHoverCard !== hoverCardTokenRef.current) return;
    clearCloseTimer();
    updatePopoverPosition();
    setIsOpen(true);
    activeWorkspaceUserHoverCard = hoverCardTokenRef.current;
  }, [clearCloseTimer, updatePopoverPosition]);

  const closePopover = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
      if (activeWorkspaceUserHoverCard === hoverCardTokenRef.current) activeWorkspaceUserHoverCard = null;
    }, hoverCardCloseDelayMs);
  }, [clearCloseTimer]);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return undefined;
    anchor.addEventListener("mouseenter", openPopover);
    anchor.addEventListener("mouseleave", closePopover);
    anchor.addEventListener("focusin", openPopover);
    anchor.addEventListener("focusout", closePopover);
    return () => {
      anchor.removeEventListener("mouseenter", openPopover);
      anchor.removeEventListener("mouseleave", closePopover);
      anchor.removeEventListener("focusin", openPopover);
      anchor.removeEventListener("focusout", closePopover);
    };
  }, [openPopover, closePopover]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const reposition = () => updatePopoverPosition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [isOpen, updatePopoverPosition]);

  useEffect(() => () => {
    clearCloseTimer();
    if (activeWorkspaceUserHoverCard === hoverCardTokenRef.current) activeWorkspaceUserHoverCard = null;
  }, [clearCloseTimer]);

  useLayoutEffect(() => {
    if (!isOpen || !popoverRef.current) return;
    updatePopoverPosition(popoverRef.current.offsetHeight, popoverRef.current.offsetWidth);
  }, [copied, isOpen, updatePopoverPosition]);

  const copyEmail = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!profile.email) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(profile.email);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = profile.email;
        textarea.setAttribute("readonly", "true");
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch (error) {
      setCopied(false);
    }
  };

  const isDarkTheme = typeof document !== "undefined" && (
    document.documentElement.dataset.workspaceAppearance === "dark"
    || Boolean(document.querySelector(".workspace-public-site.public-theme-dark"))
  );
  const popover = (
    <span
      className={`workspace-user-profile-popover is-${placement} is-open${isDarkTheme ? " is-dark" : ""}`}
      onBlur={closePopover}
      onFocus={clearCloseTimer}
      onMouseEnter={clearCloseTimer}
      onMouseLeave={closePopover}
      onMouseDown={(event) => event.stopPropagation()}
      role="dialog"
      aria-label={`${profile.name} profile`}
      ref={popoverRef}
      style={{ bottom: "auto", height: "auto", left: popoverPosition.left, position: "fixed", top: popoverPosition.top, transform: "none", width: popoverPosition.width }}
    >
      <WorkspaceUserAvatar className="workspace-user-profile-avatar" user={profile} />
      <span className="workspace-user-profile-main">
        <strong>{profile.name}</strong>
        {profile.email ? (
          <span className="workspace-user-profile-email">
            <small>{profile.email}</small>
            <button aria-label={`Copy ${profile.email}`} onClick={copyEmail} title={copied ? "Copied" : "Copy email"} type="button">
              {copied ? <CheckOutlined fontSize="inherit" /> : <ContentCopyOutlined fontSize="inherit" />}
            </button>
          </span>
        ) : null}
        <em>{profile.role}</em>
        <small>{profile.meta}</small>
      </span>
    </span>
  );

  return (
    <span className={`workspace-user-hover-card ${className}`.trim()} ref={anchorRef} tabIndex={0}>
      <span className="workspace-user-hover-card-trigger">{children || profile.name}</span>
      {isOpen && typeof document !== "undefined" ? createPortal(popover, document.body) : null}
    </span>
  );
}
